import { createStore } from '@/lib/tinyStore';
import { presenceApi } from './presenceClient';
import { emptySnapshot } from './presenceAdapter';
import { resetAfterToExpiry } from './expiry';
import {
  computeActivity, computeAutoState, pickActivity, resolveEffectiveState, toWire,
} from './presenceResolver';
import type {
  ActivityFlags, CustomStatus, ManualState, PresenceSnapshot, PresenceState, ResetAfter,
  SelfDeclaration, ShellActivityFlags,
} from './types';

const SELF_ID = 'u-me';

const initialSelf = emptySnapshot(SELF_ID);

const NO_SHELL_ACTIVITY: ShellActivityFlags = {
  inCall: false,
  presenting: false,
  screenSharing: false,
};

export const presenceStore = createStore<PresenceState>({
  selfId: SELF_ID,
  self: { ...initialSelf, autoState: 'available', effectiveState: 'available', connectionCount: 1 },
  /* Nothing has told us this person is doing anything yet. Not `available` —
     that is a conclusion, and it belongs to the resolver. */
  serverActivity: null,
  flags: { inCall: false, inMeeting: false, presenting: false, screenSharing: false, calendarBusy: false },
  shellFlags: NO_SHELL_ACTIVITY,
  automation: { autoAwayAfterMinutes: 5, dndDuringMeetings: true, dndDuringCalls: true },
  roster: {},
  connected: false,
});

/**
 * THE LAST MOMENT ANYTHING CAN VOUCH FOR A HUMAN BEING HERE.
 *
 * Kept outside React state. Fed from three places, and it only ever moves
 * FORWARD, because they answer different questions and the honest reading is
 * the latest evidence any of them has:
 *
 *   - DOM input into THIS window - pointer, key, wheel, touch and, since the
 *     owner asked for mouse MOVEMENT by name, pointermove. All a browser has.
 *     On the desktop an event only asks the shell for a fresh OS reading
 *     (hooks/useActivityMonitor.ts), unless the shell cannot answer.
 *   - `noteActivityAt`, from the desktop shell's reading of the OS. This is the
 *     one that fixes the reported complaint: a person working in VS Code or
 *     Excel delivers no DOM input to us at all, and used to age to Away with
 *     their hands on the keyboard.
 *   - `tick`, which pins it while a call is live in another shell window.
 */
let lastInputAt = 0;
/**
 * THE SAME QUESTION, ANSWERED BY THE MACHINE - `now - getSystemIdleTime()`, as
 * the desktop shell measured it. 0 in a browser, for ever, and 0 on the desktop
 * until the shell's first reading lands.
 *
 * Kept apart from `lastInputAt` rather than merged into it because the two are
 * evidence of different things and one of them can go BACKWARDS: an OS reading
 * says "the last input to any application was at T", and T is older than this
 * document's own guesswork whenever the page has just loaded next to a user who
 * is not there. Merging them into one forward-only number is what made that
 * unrepresentable - see `evidenceAt`.
 */
let lastSystemAt = 0;
/**
 * WHEN THIS DOCUMENT LOADED - the stand-in used until either sensor has spoken,
 * AND ONLY IN A DOCUMENT THAT WATCHES ITS USER.
 *
 * A fresh page knows nothing, and in the workspace "knows nothing" has to read
 * as Available: somebody just opened it. It must NOT read as "here right now"
 * for ever though, or a tab left open on an empty desk would never age to Away,
 * so the stand-in is a fixed moment rather than a rolling one.
 *
 * `assumedFrom` is that moment ONCE IT HAS BEEN GRANTED, and 0 until it is.
 * The grant comes from `assumeHumanHere`, whose only caller is
 * `measurePresenceHere()` - the same declaration that makes
 * `presenceClaim().measured` true. A desktop call window never declares, never
 * measures, and must therefore never fall back on a guess it has no way to
 * correct. This is the local half of the identical rule in
 * `lib/realtime/idleClock.ts`, which is the clock the SERVER reads; the two are
 * one fact about one person held in two modules and they are written together
 * or not at all.
 */
const loadedAt = Date.now();
let assumedFrom = 0;
/**
 * THE SCREEN IS LOCKED - Away AT ONCE, not after the idle timer.
 *
 * Separate from the clock rather than folded into it, because it is a different
 * kind of fact. Five quiet minutes is a GUESS that somebody left; a locked
 * workstation is a STATEMENT that they did, and Teams treats it as one. Folding
 * it in by winding the clock back would also be undone by the next thing that
 * touches the clock - a call window reporting in, a stray event - so the flag
 * is held and consulted, and the clock keeps meaning exactly one thing.
 *
 * Desktop only. A browser cannot see the lock screen and this is never set.
 */
let systemLocked = false;
let lastHeartbeatAt = 0;
/** One re-read at a time; see `resyncSelfDeclaration`. */
let selfResyncInFlight = false;

const HEARTBEAT_MS = 20_000;

/**
 * Idle as the RESOLVER must see it: the clock, or "gone" while the screen is
 * locked.
 *
 * Every caller of `computeAutoState` goes through here, so the lock cannot be
 * honoured on the tick and forgotten on the two flag paths - which is exactly
 * the class of bug that put a caller's own chip at Away in the middle of their
 * own call.
 */
function idleSince(now: number): number {
  if (systemLocked) return Number.MAX_SAFE_INTEGER;
  const evidence = evidenceAt();
  /*
   * NO EVIDENCE IS NOT FRESH EVIDENCE. `evidenceAt()` answers 0 when this
   * document has measured nothing and has not been granted the load-time
   * assumption, and `now - 0` is fifty-seven years of idle by accident rather
   * than on purpose - so it is said on purpose, with the same sentinel the lock
   * uses and the same one `idleClock.IDLE_ABSENT_MS` puts on the wire. The
   * alternative is the one this whole feature exists to remove: a document with
   * nothing to say reading as somebody being here.
   */
  return evidence === 0 ? Number.MAX_SAFE_INTEGER : now - evidence;
}

/**
 * THE LAST MOMENT EITHER SENSOR CAN VOUCH FOR A HUMAN BEING HERE.
 *
 * The later of the two, because they answer different questions and neither
 * subsumes the other: the OS sees every application and misses nothing this
 * document sees, but its reading is up to one poll (2 s) old, and a click that
 * landed here since is real. The disagreement between them is therefore bounded
 * by the poll period, which is 0.7% of the five-minute threshold it feeds.
 *
 * THE SEED LOSES TO A MEASUREMENT, and that is the whole reason this is not a
 * single monotonic number. `loadedAt` is an ASSUMPTION - a fresh document
 * guessing that whoever opened it is present. A page reloaded beside an empty
 * desk makes that guess too, and if the guess could out-vote the shell's "the
 * last input to this machine was fifty minutes ago" it would never be
 * corrected: OS idle only ever grows while nobody is there, so every later
 * reading would lose to the same seed as well. Reported Available, for ever,
 * to the whole workspace.
 *
 * AND THE SEED ITSELF IS NOT FREE. It is worth having in the document a human
 * opened and worth nothing in a document the SHELL opened, which is why it is
 * `assumedFrom` and not `loadedAt` that is read here. See `assumeHumanHere`.
 */
function evidenceAt(): number {
  const measured = Math.max(lastInputAt, lastSystemAt);
  if (measured !== 0) return measured;
  /* An UNGRANTED assumption is not an assumption. 0 means "nothing at all",
     which `idleSince` turns into the absence sentinel rather than into a
     timestamp from 1970 that would read as a human being here. */
  return assumedFrom;
}

/**
 * THE SERVER KNOWS SOMETHING THIS TAB DECLARED AND NEVER HEARD ABOUT — GO ASK.
 *
 * `PresenceSummary`, the shape of every presence frame and of `/presence/bulk`,
 * carries the ANSWER and three of its inputs. It does not carry the DECLARED
 * ones: `manual_state`, its expiry, the out-of-office window. Those reach this
 * client through exactly one door, `GET /presence/me`, and `PresenceRuntime`
 * opens it once per session. So every way a declaration can change that is not
 * this tab's own menu click left `state.self` resolving from a stale
 * declaration for the rest of the session:
 *
 *   • typing `/dnd` in the composer (services/commands.ts sets `manual_state`
 *     server-side and publishes; nothing on the web side handles the reply);
 *   • picking Appear offline, Busy, Away or Be right back on a SECOND device;
 *   • a scheduled out-of-office window opening while the tab is up;
 *   • a manual override lapsing server-side, swept by the API.
 *
 * All four sit ABOVE the activity tier in both resolvers, so the symptom was
 * always the same shape: the whole workspace correctly saw Do not disturb /
 * Offline / Out of office while the owner's own chip, menu, profile card and
 * tray icon said "In a call". Six such disagreements were reproduced against
 * this store.
 *
 * The fix is not to guess the declaration from the answer — it cannot be done,
 * `do_not_disturb` and a real DND-during-a-call are the same wire value — but to
 * NOTICE that the two ends disagree and re-read the door that has all of them.
 *
 * Bounded by ONE request in flight rather than by a clock. A time window would
 * be the wrong shape: presence deltas for yourself arrive on CHANGE, a handful
 * of times an hour, so there is no storm to throttle — while a window that
 * happened to swallow the one delta that mattered would leave the chip wrong
 * with nothing scheduled to fix it, which is the failure mode being repaired.
 */
function resyncSelfDeclaration(): void {
  if (selfResyncInFlight) return;
  selfResyncInFlight = true;
  void presenceApi
    .fetchSelf()
    .then((declaration) => presenceActions.hydrateSelf(declaration))
    .catch(() => undefined)
    .finally(() => {
      selfResyncInFlight = false;
    });
}

/**
 * EVERYTHING THIS PERSON IS DOING — in this document AND in the shell's others.
 *
 * `flags` is what THIS document is doing; `shellFlags` is what the desktop
 * shell's call windows report. Neither may overwrite the other — a call window
 * closing would otherwise clear this document's own voice channel, and this
 * document's own state would clear a second call window's — so they are ORed at
 * the point of use and nowhere else. In a browser `shellFlags` is all-false for
 * the life of the tab and this is the identity function.
 */
function activeFlags(state: PresenceState): ActivityFlags {
  const shell = state.shellFlags;
  return {
    ...state.flags,
    inCall: state.flags.inCall || shell.inCall,
    presenting: state.flags.presenting || shell.presenting,
    screenSharing: state.flags.screenSharing || shell.screenSharing,
  };
}

/**
 * HAS THIS DOCUMENT ACTUALLY MEASURED A HUMAN, EVER?
 *
 * Not "is the user here" and not "does this document watch them" - the third
 * question, which is whether either sensor has yet produced a single reading.
 * Read by `presenceClaim().observed` (features/presence/systemActivity.ts) so
 * that the guess a fresh page starts from can ride the `ping`, where it ages
 * out on its own, and never the `active` frame, which latches.
 *
 * THERE ARE TWO IDLE CLOCKS, they are written in different modules, and only
 * one of them is read by anything outside this tab. Both are stated here
 * because keeping one and forgetting the other has been the shape of every
 * defect this feature has had:
 *
 *   - `lastInputAt` / `lastSystemAt`, above - local, feed `computeAutoState`
 *     and this user's own chip and tray icon. NOTHING PINS THEM: only measured
 *     input writes them.
 *   - `createRealtimeClient`'s own idle clock, sent to the gateway as
 *     `{t:'ping', idleMs}` every ~25 s and the ONLY idle signal the server ever
 *     sees (gateway `core/connection.ts`: "Drives auto-away without extra
 *     frames"). Refreshed by `noteActivity()` on pointer/key input.
 *
 * A THIRD SOURCE FEEDS BOTH OF THEM ON THE DESKTOP: the shell's reading of the
 * OS idle time (`features/presence/systemActivity.ts`), which is written into
 * both in one gesture for the same reason - the shell reports one number and
 * BOTH clocks have to be given it, or the server goes on believing this socket
 * is idle while the tab believes its user is at the keyboard. A browser has no
 * such source and keeps the DOM listeners. The load-time ASSUMPTION and the
 * LOCK are granted to both the same way, and for the same reason.
 *
 * `shellActivityIsLive()` used to live here, exported so that a live call in
 * another shell window could pin the transport clock every 15 s. It is gone
 * with its two callers: a fact about a WINDOW may not be written into a slot
 * that holds MEASUREMENTS, and the chip it was protecting is protected one tier
 * up by `resolveEffectiveState` branch 5, which returns `in_a_call` above the
 * auto state whatever the clock says.
 */
export function selfHasEvidence(): boolean {
  return Math.max(lastInputAt, lastSystemAt) !== 0;
}

/**
 * IS THIS DOCUMENT'S EVIDENCE THAT NOBODY IS AT THE MACHINE?
 *
 * THE ONE ANSWER BOTH CLOCKS ARE DRAWN FROM. The local resolver already had it
 * - `idleSince` against the user's own `auto_away_after_minutes` is what puts
 * this person's own chip at Away - but the frames sent to the gateway were
 * decided somewhere else, from something else, and drifted: this tab read
 * `away` while every colleague read `available` in four separate reproductions
 * (a lock, a reconnect, a reload beside an empty desk, and a machine woken by a
 * scheduled task). Asking one question in one place is what stops that
 * recurring, and it is why the claim is computed HERE, next to the evidence,
 * rather than in the transport that merely carries it.
 *
 * The threshold is `computeAutoState`'s, character for character
 * (presenceResolver.ts): `idleFor >= autoAwayAfterMinutes * 60_000`. It
 * deliberately does NOT go through `computeAutoState` itself, because that
 * function answers `busy` for a calendar-busy user BEFORE it looks at the idle
 * time - correct for a label, wrong for this question, and it would have let a
 * locked machine with a meeting in the diary go on claiming a human.
 *
 * `idleSince` is what makes a locked screen answer true here whatever the clock
 * says, and what keeps a live call in another shell window answering false.
 */
export function selfIsIdle(now: number = Date.now()): boolean {
  const { automation } = presenceStore.get();
  return idleSince(now) >= automation.autoAwayAfterMinutes * 60_000;
}

/**
 * THE ONE PLACE `self.effectiveState` IS PRODUCED.
 *
 * Every path — the tick, a flag change, the shell, a manual override, a server
 * delta — comes through here, so there is exactly one resolution of the
 * signed-in user's presence and nothing can quietly compute a second one. That
 * is what makes the client/server invariant enforceable: `resolveEffectiveState`
 * is a branch-for-branch mirror of `cyphex.presence_resolve()`, so the two can
 * only ever differ by an INPUT this tab owns and the server cannot see (the idle
 * clock behind `autoState`, the socket behind `connectionCount`) — never by a
 * rule. See presenceResolver.ts.
 *
 * ONE ACTIVITY, FROM THE TWO PLACES IT CAN COME FROM. `serverActivity` is
 * `user_presence.activity_state`, derived by trigger from the participant tables
 * and authoritative for everybody; `computeActivity(activeFlags(state))` is what
 * THIS document and the shell's call windows are doing, which covers the seconds
 * between joining and the participant row landing. It is deliberately NOT taken
 * from `patch`: a caller cannot hand-set the activity here, because the moment a
 * local flag could outlive the server's view of it we would be back to the stuck
 * `inCall` failure infra/sql/062 was written to end.
 */
function reduceSelf(state: PresenceState, patch: Partial<PresenceSnapshot>, now: number): PresenceState {
  const activityState = pickActivity(state.serverActivity, computeActivity(activeFlags(state)));
  const merged: PresenceSnapshot = { ...state.self, ...patch, activityState };
  const effectiveState = resolveEffectiveState(merged, now);
  const next: PresenceSnapshot = {
    ...merged,
    effectiveState,
    stateSince: effectiveState === state.self.effectiveState ? state.self.stateSince : now,
  };
  return { ...state, self: next };
}

export const presenceActions = {
  /**
   * Binds the store to the signed-in user once the viewer query resolves.
   * Until then the seeded placeholder id is used, so the shell can render
   * a presence dot without waiting on a request.
   */
  setSelfId(userId: string): void {
    presenceStore.set((state) =>
      state.selfId === userId
        ? state
        : { ...state, selfId: userId, self: { ...state.self, userId } },
    );
  },

  /**
   * Adopt what the server says this user declared.
   *
   * ONLY the declaration, never the activity: this tab is the authority on
   * whether a human is here, so `autoState` and `connectionCount` stay local.
   * Taking the server's connection count instead would paint the chip Offline
   * for the second between the page rendering and the socket opening.
   */
  hydrateSelf(declaration: SelfDeclaration): void {
    const now = Date.now();
    presenceStore.set((state) =>
      reduceSelf(
        {
          ...state,
          selfId: declaration.userId,
          automation: declaration.automation,
          /* `GET /presence/me` is the ONE server-owned activity fact a cold load
             can have. Without it a reload during a call showed the owner his own
             chip as Available until the first socket frame arrived. */
          serverActivity: declaration.activityState,
        },
        {
          userId: declaration.userId,
          manualState: declaration.manualState,
          manualExpiresAt: declaration.manualExpiresAt,
          status: declaration.status,
          ooo: declaration.ooo,
          /*
           * THE SERVER HAS NOW TOLD US. Without this the snapshot stayed at
           * `emptySnapshot`'s `resolved: false` for the whole session, and the
           * presence menu — which renders `self.resolved ? label : 'Checking…'`
           * — sat on "Checking…" for ever, even though the answer had already
           * arrived and been stored beside it.
           */
          resolved: true,
        },
        now,
      ),
    );
  },

  markActivity(): void {
    lastInputAt = Date.now();
  },

  /**
   * A HUMAN OPENED THIS DOCUMENT, so this store may fall back on saying so.
   *
   * The local twin of `idleClock.assumeOpenedByHuman()`, granted in the same
   * gesture by `measurePresenceHere()` so the two clocks cannot come to hold
   * different opinions about whether this document is entitled to guess. The
   * instant is `loadedAt` and is NEVER refreshed: a guess that renewed itself
   * would be a measurement, which is exactly the confusion this file has now
   * spent three rounds taking apart.
   */
  assumeHumanHere(): void {
    assumedFrom = loadedAt;
  },

  /**
   * FORGET EVERY IDLE MEASUREMENT THIS DOCUMENT HAS MADE.
   *
   * Both sensors go back to "nothing measured", so `evidenceAt` falls back to
   * the load-time assumption again, and the lock is cleared. It is the honest
   * thing to do whenever the PERSON this document belongs to may have changed
   * or its readings may no longer describe them: one user's idle evidence is
   * not another's, and a stale lock would paint the next one Away.
   *
   * Called from `resetSystemActivity`, which owns the other half of the same
   * state, so there is one gesture rather than two that can drift apart.
   */
  forgetIdleEvidence(): void {
    lastInputAt = 0;
    lastSystemAt = 0;
    systemLocked = false;
    /* `assumedFrom` survives, for the reason `measuring` does: it describes
       this DOCUMENT, not the person in front of it, and `PresenceRuntime` is
       still mounted. It does not move either, so the next person inherits a
       guess that is already as old as the tab. */
  },

  /**
   * A HUMAN WAS HERE AT `at` - the desktop shell's OS reading, folded in.
   *
   * `at` is `now - powerMonitor.getSystemIdleTime()`, i.e. the last input to
   * ANY application, which is the fact Microsoft Teams uses and the one this
   * window cannot obtain for itself. FORWARD ONLY: a sample taken a poll ago
   * must never pull the clock back over a keystroke that landed after it.
   *
   * The caller feeds `getRealtime().noteActivity(at)` with the same number.
   * There are two clocks, the server only ever sees the other one, and a
   * previous round was caught pinning one and leaving the other idle.
   */
  noteActivityAt(at: number): void {
    /* Monotonic across the OS sensor ALONE. Reports are stamped in the shell
       and the shell's bus buffers across a reload, so an older one can arrive
       after a newer one; while nobody is touching the machine `at` is a
       CONSTANT (the moment of the last input), so this is a no-op exactly when
       it should be and moves forward exactly when somebody does something. */
    if (at > lastSystemAt) lastSystemAt = at;
  },

  /**
   * The workstation was locked or unlocked.
   *
   * Locking is Away IMMEDIATELY - see `systemLocked`. Both directions resolve
   * at once rather than waiting up to 15 s for the tick: these are the two
   * moments a person watches their own chip.
   *
   * IT WRITES NO CLOCK, IN EITHER DIRECTION, AND THAT IS THE FIX.
   *
   * "Not locked" used to stamp `lastInputAt = Date.now()` on the reasoning that
   * unlocking proves a human is at the machine. Unlocking does. THIS FUNCTION
   * IS NOT UNLOCKING: it is called on every non-`locked` reading the shell
   * takes, which is most of them, and a single `unknown` sample - the answer
   * `getSystemIdleState()` gives when it throws or cannot tell - therefore
   * cleared the lock AND asserted a keystroke, painting this user's own chip
   * and tray icon Available with the screen still black. Reproduced.
   *
   * The evidence for an unlock is already on its way in and is a MEASUREMENT:
   * typing a password is input, so the same report carries `idleMs` near zero
   * and `noteActivityAt` folds it in honestly, from the shell's clock rather
   * than from a `Date.now()` invented here. `unknown` is now filtered out
   * upstream (features/presence/systemActivity.ts) and could not reach this
   * either way - two independent reasons, because this is the expensive
   * direction to be wrong in.
   */
  setSystemLocked(locked: boolean): void {
    if (systemLocked === locked) return;
    systemLocked = locked;
    presenceActions.tick();
  },

  /**
   * Window hidden or shown. IT WRITES NO CLOCK IN EITHER DIRECTION.
   *
   * Hiding never did - Teams makes you Away for going quiet or locking your
   * screen, never for alt-tabbing. SHOWING used to, on the reasoning that
   * coming back to a tab is the user being back, and that was the last
   * assumption left sitting in a measurement slot.
   *
   * A document becoming visible is not input. The OS un-occludes a window when
   * the app in front of it closes; a session restore paints every tab; a screen
   * unlock repaints the lot. Executed against fifty minutes of measured OS
   * idle, one hidden -> visible transition with nobody at the machine put this
   * user's OWN chip and OWN tray icon back to Available - permanently, because
   * `lastInputAt` only moves forward and the OS could never afterwards
   * contradict it. It also made the two clocks disagree for five minutes: the
   * transport clock has no slot this touched, so the owner read Available while
   * every colleague read Away, which is the exact split this feature exists to
   * remove.
   *
   * NOTHING REPLACES IT, and the cost is bounded and one-directional: a user
   * who alt-tabs back to an already-Away tab and then touches nothing stays
   * Away until they do. `useActivityMonitor` is listening for pointer, key,
   * wheel and touch, so "until they do" is normally the same gesture that
   * brought them back. Being briefly Away while present is a green dot late;
   * the line this replaces was a green dot on an empty desk.
   */
  setTabHidden(_hidden: boolean): void {
    /* Recorded by `windowVisible` in features/presence/systemActivity.ts, which
       is a fact about a WINDOW and is reported as one (`background`). */
  },

  /** Single ticker: recomputes the automatic state, expires overrides, heartbeats. */
  tick(now = Date.now()): void {
    const state = presenceStore.get();
    /*
     * A CALL IN ANOTHER WINDOW IS NOT A MEASUREMENT OF A HUMAN, AND IT USED TO
     * BE WRITTEN INTO THE SLOT WHERE ONLY MEASUREMENTS BELONG.
     *
     * `if (shellIsLive(state.shellFlags)) lastInputAt = now;` stood here and on
     * the flag path below. It is a fact about a WINDOW EXISTING, restamped
     * every 15 s into a forward-only clock, so the pin was always newer than
     * any reading the OS could take: executed, fifty minutes of measured
     * absence lost to it on every beat and the server flipped away ->
     * available from the flag alone.
     *
     * IT WAS ALSO UNNECESSARY. The comment it replaces argued that without the
     * pin a caller's own chip goes Away mid-call - but the resolver already
     * handles that one tier up: `resolveEffectiveState` branch 5 returns
     * `in_a_call` VERBATIM, above the auto state, and its own header records
     * why ("the server sends auto_state = away beside activity_state =
     * in_a_call for a person who is talking right now"). The chip is safe
     * without a fake keystroke; `test/presence.test.mjs` holds that branch
     * against a fixture from the live `cyphex.presence_resolve()`.
     *
     * What the pin really bought was FRESHNESS AFTER the call - and that is the
     * half that must not be manufactured. A person who listened for fifty
     * minutes without touching anything has been idle for fifty minutes, and on
     * the desktop the shell is measuring exactly that; if they were typing, the
     * OS reading says so on its own.
     */
    const idleMs = idleSince(now);

    const manualExpired =
      state.self.manualState !== null &&
      state.self.manualExpiresAt !== null &&
      state.self.manualExpiresAt <= now;

    const statusExpired =
      state.self.status?.expiresAt != null && state.self.status.expiresAt <= now;

    const autoState = computeAutoState(
      activeFlags(state), state.automation, idleMs, now, evidenceAt(),
    );

    /* `null`, not 0, when this document has nothing: `lastActivityAt` is what
       draws "Last seen ..." and the epoch would render as 1970. */
    const evidence = evidenceAt();
    const patch: Partial<PresenceSnapshot> = {
      autoState,
      lastActivityAt: evidence === 0 ? null : evidence,
    };
    if (manualExpired) {
      patch.manualState = null;
      patch.manualExpiresAt = null;
    }
    if (statusExpired) patch.status = null;

    const unchanged =
      autoState === state.self.autoState && !manualExpired && !statusExpired;
    if (!unchanged) presenceStore.set(reduceSelf(state, patch, now));

    if (now - lastHeartbeatAt >= HEARTBEAT_MS) {
      lastHeartbeatAt = now;
      void presenceApi.heartbeat(toWire(autoState)).catch(() => undefined);
    }
  },

  /** What THIS document is doing. Never write another window's state here. */
  setFlags(patch: Partial<ActivityFlags>): void {
    const state = presenceStore.get();
    const flags = { ...state.flags, ...patch };
    const now = Date.now();
    const next = { ...state, flags };
    const autoState = computeAutoState(
      activeFlags(next), state.automation, idleSince(now), now, evidenceAt(),
    );
    presenceStore.set(reduceSelf(next, { autoState }, now));
  },

  /**
   * WHAT THE DESKTOP SHELL'S OTHER WINDOWS ARE DOING — the whole aggregate, not
   * a patch.
   *
   * Set only by the workspace window, from the shell, which owns one row per
   * live call window. An aggregate because two call windows can be up at once
   * and one closing must not be able to say "not in a call" on the other's
   * behalf — the shell answers that from what is actually alive, which is the
   * only place it can be answered correctly.
   *
   * THIS IS A LOCAL MIRROR, NOT A PRESENCE INPUT, and the distinction matters
   * because the comment that used to sit here got it backwards. Nothing this
   * store computes reaches the server: `presenceApi.heartbeat()` is an empty
   * function on both adapters and there is no route behind it, and
   * `user_presence.activity_state` — the `p_activity` that infra/sql/065, 067
   * and 068 resolve from — is derived by trigger from `call_participants` and is
   * documented "Never client-set". Colleagues saw In a call throughout.
   *
   * What was actually broken is here, locally: `applyDeltas` writes the server's
   * `in_a_call` into `state.self`, and `tick()` then recomputed `autoState` from
   * an idle clock nothing had touched for five minutes and overwrote it with
   * `away`. The caller's OWN chip and OWN tray icon went Away mid-call. This is
   * what stops that.
   */
  setShellFlags(next: ShellActivityFlags): void {
    const state = presenceStore.get();
    const shellFlags: ShellActivityFlags = {
      inCall: next.inCall,
      presenting: next.presenting,
      screenSharing: next.screenSharing,
    };
    const current = state.shellFlags;
    if (
      current.inCall === shellFlags.inCall &&
      current.presenting === shellFlags.presenting &&
      current.screenSharing === shellFlags.screenSharing
    ) {
      return;
    }
    const now = Date.now();
    /* No clock is written here either - see `tick`. Answering a call IS input,
       but it is input the OS measured and the shell is already reporting; this
       flag is a fact about a window and is worth exactly that. */
    const merged = { ...state, shellFlags };
    const autoState = computeAutoState(
      activeFlags(merged), state.automation, idleSince(now), now, evidenceAt(),
    );
    presenceStore.set(reduceSelf(merged, { autoState }, now));
  },

  setManualState(manualState: ManualState | null, resetAfter: ResetAfter = 'never'): void {
    const now = Date.now();
    const manualExpiresAt = manualState ? resetAfterToExpiry(resetAfter, now) : null;
    presenceStore.set(reduceSelf(presenceStore.get(), { manualState, manualExpiresAt }, now));
    void presenceApi.setManualState(manualState, resetAfter).catch(() => undefined);
  },

  setCustomStatus(status: CustomStatus | null): void {
    presenceStore.set(reduceSelf(presenceStore.get(), { status }, Date.now()));
    void presenceApi.setCustomStatus(status).catch(() => undefined);
  },

  applyDeltas(rows: readonly PresenceSnapshot[]): void {
    if (rows.length === 0) return;
    const now = Date.now();
    /* Set inside the updater, acted on after it: `presenceStore.set` runs its
       updater synchronously, and a reducer that fires requests is a reducer
       nobody can reason about. */
    let selfDisagrees = false;
    presenceStore.set((state) => {
      const roster = { ...state.roster };
      for (const row of rows) roster[row.userId] = row;

      /*
       * YOUR OWN PRESENCE IS A DELTA LIKE ANYONE ELSE'S — APPLY IT TO SELF.
       *
       * These rows only ever reached `roster`, and the chip in the top bar,
       * the presence menu and the profile card all read `state.self`. So the
       * server could resolve you to `in_a_call` or `presenting`, publish it,
       * and every colleague would see the red badge while YOUR view of
       * yourself never moved. The same gap kept `resolved` false, which is the
       * other half of the "Checking…" the menu was stuck on.
       *
       * ONE SERVER-OWNED INPUT IS ADOPTED, AND IT IS THE ONLY ONE ON THE WIRE.
       *
       * It used to copy the server's `effectiveState` straight across and leave
       * the local resolver to overwrite it on the next 15s tick — two resolutions
       * of one fact, racing, and the local one blind to the activity. Then it
       * adopted `autoState` and `connectionCount` as well, "the server's INPUTS",
       * and that comment was false in the way that matters: `PresenceSummary`
       * carries NEITHER. `presenceHttp.fromSummary` writes the RESOLVED state
       * into `autoState` and INVENTS `connectionCount` as `state === 'offline'
       * ? 0 : 1`. Adopting the second one is what pinned the owner's own chip:
       * one cold `/presence/bulk` that answered `offline` — which the API did,
       * verbatim off a durable projection whose connection count only ever
       * counts down — wrote `connectionCount: 0` into `self`, branch 4 of the
       * resolver answered `offline` from then on, and NOTHING ever wrote it back
       * up. `tick()` patches `autoState` and `lastActivityAt`; `setConnected`
       * touches `state.connected`. The chip stayed Offline for the session.
       *
       * So: `activity_state` is adopted, because it is the one fact here the
       * server genuinely owns and this tab genuinely cannot see — a call in
       * another window of the desktop shell — and 062 forbids the client
       * asserting it. `autoState` and `connectionCount` stay LOCAL, which is the
       * policy `hydrateSelf` and types.ts have always stated: this tab knows
       * whether a human is touching it and whether it holds a socket, and the
       * server does not. Taking the server's word for either is how you get told
       * you are offline while you are typing.
       *
       * The DECLARED fields — manualState, status, ooo — cannot be adopted from
       * here at all: they are not on the wire. When the answer proves this tab's
       * copy of them is stale, `resyncSelfDeclaration` goes and reads them.
       */
      const mine = state.selfId === null ? undefined : rows.find((row) => row.userId === state.selfId);
      const withRoster = { ...state, roster };
      if (mine === undefined) return withRoster;

      const next = reduceSelf(
        { ...withRoster, serverActivity: mine.activityState },
        {
          lastActivityAt: mine.lastActivityAt,
          resolved: true,
        },
        now,
      );

      /* Compared on the WIRE, so the client-only refinement `screenSharing` —
         which `toWire` collapses into `presenting` — is not mistaken for a
         disagreement and does not ask the same question every four seconds. */
      selfDisagrees = toWire(next.self.effectiveState) !== toWire(mine.effectiveState);

      /* WHEN it started belongs to the server whenever the two agree about WHAT
         it is: "In a call for 12m" counts from the moment the participant row
         landed, not from the moment this tab noticed. Where they disagree the
         difference is a tab-owned input — the idle clock or the socket count —
         and the local clock is the honest one for it. */
      return next.self.effectiveState === mine.effectiveState
        ? { ...next, self: { ...next.self, stateSince: mine.stateSince } }
        : next;
    });
    if (selfDisagrees) resyncSelfDeclaration();
  },

  setConnected(connected: boolean): void {
    presenceStore.set((state) => (state.connected === connected ? state : { ...state, connected }));
  },
};

/* ── Stable selectors (required by useStoreValue) ──────────────────────────── */
/**
 * ONE PERSON, ONE PRESENCE — INCLUDING YOURSELF.
 *
 * `PresenceRuntime` subscribes you to your own `presence:<id>` topic, so your id
 * is in `roster` as well as in `self`. Two stored snapshots for one human is two
 * answers to one question, and they are not interchangeable: `self` carries this
 * tab's idle clock and its live socket, `roster[selfId]` carries the server's
 * lagging `auto_state` and a `connection_count` that only ever counts down
 * (infra/sql/065). A member list drawing you from the roster while the top-bar
 * chip drew you from `self` could put your own face on screen twice with two
 * different dots. Every per-person read goes through here instead.
 */
export function snapshotOf(state: PresenceState, userId: string): PresenceSnapshot | null {
  return userId === state.selfId ? state.self : (state.roster[userId] ?? null);
}

export const selectSelf = (state: PresenceState): PresenceSnapshot => state.self;
export const selectSelfState = (state: PresenceState) => state.self.effectiveState;
export const selectSelfStatus = (state: PresenceState) => state.self.status;
export const selectConnected = (state: PresenceState): boolean => state.connected;
export const selectRoster = (state: PresenceState) => state.roster;
